import React, { useCallback, useMemo } from "react";
import cx from "classnames";
import { uniqueId } from "lodash-es";
import s from "./Switcher.module.scss";

interface SwitcherProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: React.ReactNode;
  id?: string;
  className?: string;
}

export const Switcher: React.FC<SwitcherProps> = React.memo(
  ({ checked = false, onChange, disabled = false, label, id, className }) => {
    const inputId = useMemo(() => id ?? uniqueId("switcher-"), [id]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!disabled) onChange?.(e.target.checked);
      },
      [disabled, onChange],
    );

    return (
      <div
        className={cx(
          "d-inline-flex align-items-center gap-2 user-select-none",
          { [s.disabled]: disabled },
          className,
        )}
      >
        <label
          className={cx(
            s.track,
            "position-relative d-inline-flex align-items-center flex-shrink-0",
            {
              [s.checked]: checked,
              [s.disabled]: disabled,
            },
          )}
          htmlFor={inputId}
          aria-checked={checked}
          role="switch"
        >
          <input
            id={inputId}
            type="checkbox"
            className={cx(s.hiddenInput, "position-absolute pe-none")}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
          />
          <span
            className={cx(s.thumb, "position-absolute rounded-circle bg-white")}
          />
        </label>
        {label && (
          <label htmlFor={inputId} className={s.label}>
            {label}
          </label>
        )}
      </div>
    );
  },
);

Switcher.displayName = "Switcher";
